import { z } from "zod";
import {
  REASONING_SCHEMA,
  type ReasoningApi,
  type ReasoningChainView,
  type ReasoningCommandInput,
  type ReasoningOperation,
  type ReasoningReceipt,
  type ReasoningSessionResponse,
} from "~/api/reasoningTypes";

/**
 * The machine-readable `error` code a refusal carried (`{ error, message }`), when the transport
 * kept the body on the thrown error (`KnowItApi.errorOf` does; the fake mirrors it). A surface
 * branches on `session-revision-conflict` and the like through this, never on the sentence.
 */
export function reasoningErrorCode(error: unknown): string | null {
  const body = (error as { body?: unknown } | null)?.body;
  const code = (body as { error?: unknown } | null)?.error;
  return typeof code === "string" && code ? code : null;
}

export interface ReasoningTransport {
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
  get(path: string): Promise<unknown>;
}

const command = (input: ReasoningCommandInput) => ({
  ...input,
  schemaVersion: REASONING_SCHEMA,
  commandId: input.commandId ?? crypto.randomUUID(),
});
const receipt = (value: unknown): ReasoningReceipt =>
  (value as { receipt: ReasoningReceipt }).receipt;

/** `ReasoningController.ProviderReceiptMetadata`: an object, `{unreadable:true}`, or null. */
const providerReceipt = z
  .object({
    requestId: z.string().nullable().optional(),
    model: z.string().nullable().optional(),
    inputTokens: z.number().int().nullable().optional(),
    outputTokens: z.number().int().nullable().optional(),
    durationMs: z.number().nullable().optional(),
    effortMode: z.string().nullable().optional(),
    outputLimit: z.number().int().nullable().optional(),
    truncated: z.boolean().nullable().optional(),
    fullTextDigest: z.string().nullable().optional(),
    error: z.string().nullable().optional(),
    unreadable: z.boolean().optional(),
  })
  .passthrough()
  .nullable();
/** The operation row every operation-bearing response carries (trace list, execute receipt). */
const operationRow = z
  .object({
    id: z.string(),
    status: z.string(),
    thinkerId: z.string(),
    candidateRecordId: z.string().nullable(),
    evaluationRecordId: z.string().nullable(),
    error: z.string().nullable(),
    requestedModel: z.string(),
    requestedEffort: z.string(),
    reservedTokens: z.number().int().nonnegative(),
    actualInputTokens: z.number().int().nonnegative(),
    actualOutputTokens: z.number().int().nonnegative(),
    contextRecordId: z.string().optional(),
    criteriaDigest: z.string().optional(),
    dependencyEpoch: z.number().int().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough();
/** `POST /api/reasoning.trace` — the exact shape ReasoningController.Trace returns. */
export const traceResponse = z.object({
  trace: z.object({
    events: z.array(
      z
        .object({
          id: z.string(),
          sequence: z.number().int().nonnegative(),
          kind: z.string(),
          payload: z.record(z.string(), z.unknown()),
          commandId: z.string().optional(),
          actorId: z.string().optional(),
          recordId: z.string().nullable().optional(),
          occurredAt: z.string().optional(),
        })
        .passthrough(),
    ),
    nextSequence: z.number().int().nonnegative().nullable(),
  }),
  traceCoverage: z.string(),
  traceManifest: z
    .object({
      scope: z.string().optional(),
      eventSequenceHighWatermark: z.number().int().nonnegative(),
      recordedEventCount: z.number().int().nonnegative(),
      missingRanges: z.array(z.object({ from: z.number().int(), to: z.number().int() })),
      missingRangeCount: z.number().int().nonnegative(),
      providerReceiptGapCount: z.number().int().nonnegative(),
      operationJournalGapCount: z.number().int().nonnegative(),
      responseOmits: z.array(z.string()).optional(),
    })
    .passthrough(),
  operations: z.array(
    operationRow.extend({
      receiptGap: z.boolean(),
      journalGap: z.boolean(),
      generation: providerReceipt,
      evaluation: providerReceipt,
    }),
  ),
});
const recordView = z
  .object({
    id: z.string(),
    kind: z.string(),
    logicalId: z.string(),
    revision: z.number().int(),
    schemaVersion: z.string(),
    payloadDigest: z.string(),
    payload: z.record(z.string(), z.unknown()),
    actorId: z.string(),
    createdAt: z.string(),
    eligible: z.boolean(),
    ineligibilityReason: z.string().nullable(),
  })
  .passthrough();
const sessionView = z
  .object({
    id: z.string(),
    runId: z.string(),
    workItemId: z.string().nullable(),
    schemaVersion: z.string(),
    strategyVersion: z.string(),
    revision: z.number().int(),
    dependencyEpoch: z.number().int(),
    state: z.string(),
    criteriaDigest: z.string(),
    criteria: z.array(
      z
        .object({
          id: z.string(),
          description: z.string(),
          hard: z.boolean(),
          requiresEvidence: z.boolean(),
        })
        .passthrough(),
    ),
    contextRecordId: z.string(),
    currentAcceptanceRecordId: z.string().nullable(),
    deadline: z.string().nullable().optional(),
    records: z.array(recordView),
  })
  .passthrough();
/** `POST /api/reasoning.get` and `.by-run` — `ReasoningSessionView` plus the pinned sources' barriers. */
export const sessionResponse = z.object({
  session: sessionView.nullable(),
  sourceBarriers: z
    .array(
      z
        .object({
          sourceId: z.string(),
          epoch: z.number().int(),
          tombstone: z.boolean(),
          revision: z.string().nullable().optional(),
          contentDigest: z.string().nullable().optional(),
        })
        .passthrough(),
    )
    .default([]),
});
/** The execute receipt is the operation row itself; normalise it like a trace operation. */
const normaliseOperation = (
  row: z.infer<typeof operationRow>,
  sessionId: string,
): ReasoningOperation & { id: string } => ({
  ...row,
  operationId: row.id,
  sessionId,
  inputTokens: row.actualInputTokens,
  outputTokens: row.actualOutputTokens,
});
const evidenceRef = z
  .object({
    sourceKind: z.string(),
    sourceId: z.string(),
    revision: z.string(),
    contentDigest: z.string(),
    observedEpoch: z.number().int().optional(),
    expiresAt: z.string().nullable().optional(),
  })
  .passthrough();
/** `POST /api/reasoning.chain` — `ReasoningChainView` (KnowIt.Application/Reasoning/ReasoningChain.cs). */
export const chainResponse = z.object({
  sessionId: z.string(),
  sessionRevision: z.number().int().nonnegative(),
  entries: z.array(
    z
      .object({
        id: z.string(),
        kind: z.enum(["step", "candidate", "thinking"]),
        stepKind: z.string().nullable(),
        summary: z.string(),
        rationale: z.string().nullable(),
        origin: z.enum(["actor-report", "model-emitted", "server-operation"]),
        createdAt: z.string(),
        position: z.number().int(),
        recordId: z.string().nullable(),
        operationId: z.string().nullable(),
        candidateRecordId: z.string().nullable(),
        supersededByRecordId: z.string().nullable(),
        struck: z.boolean(),
        revisionOfId: z.string().nullable(),
        insertedAfterId: z.string().nullable(),
        eligible: z.boolean(),
        truncated: z.boolean(),
        evidence: z.array(evidenceRef).default([]),
      })
      .passthrough(),
  ),
  effective: z.array(z.string()),
  digest: z.string(),
});
export const graphResponse = z.object({
  status: z.enum(["current", "lagging", "unavailable"]),
  expectedRevision: z.number().int().nonnegative(),
  projectedRevision: z.number().int().nonnegative().nullable(),
  expectedEventSequence: z.number().int().nonnegative(),
  projectedEventSequence: z.number().int().nonnegative().nullable(),
  expectedRecords: z.number().int().nonnegative(),
  nodes: z.array(
    z.object({
      id: z.string(),
      kind: z.string(),
      label: z.string(),
      revision: z.number().int().nullable(),
      digest: z.string().nullable(),
    }),
  ),
  edges: z.array(
    z.object({
      source: z.string(),
      target: z.string(),
      kind: z.string(),
      revision: z.string().nullable(),
      digest: z.string().nullable(),
    }),
  ),
});

/** One authenticated SDK for browser clients and cross-system callers. Retry with the same commandId. */
export function createKnowItReasoning(transport: ReasoningTransport): ReasoningApi {
  const post = (name: string, body: Record<string, unknown>) =>
    transport.post(`reasoning.${name}`, body);
  return {
    importSource: (input) =>
      post("source.import", { ...input }) as ReturnType<ReasoningApi["importSource"]>,
    getSource: (documentId) =>
      post("source.get", { documentId }) as ReturnType<ReasoningApi["getSource"]>,
    startSearch: (input) =>
      post("search.start", command(input)) as ReturnType<ReasoningApi["startSearch"]>,
    advanceSearch: (input) =>
      post("search.advance", {
        ...input,
        commandId: input.commandId ?? crypto.randomUUID(),
      }) as ReturnType<ReasoningApi["advanceSearch"]>,
    getSearch: (sessionId) =>
      post("search.get", { sessionId }) as ReturnType<ReasoningApi["getSearch"]>,
    retainArtifact: async (input) => receipt(await post("artifact.retain", command(input))),
    readArtifact: (sessionId, recordId) =>
      post("artifact.read", { sessionId, recordId }) as ReturnType<ReasoningApi["readArtifact"]>,
    localCapabilities: (thinkerId) =>
      post("local-capabilities", { thinkerId }) as ReturnType<ReasoningApi["localCapabilities"]>,
    support: () => transport.get("reasoning.support") as ReturnType<ReasoningApi["support"]>,
    request: (input) =>
      post("request", {
        ...input,
        commandId: input.commandId ?? crypto.randomUUID(),
      }) as ReturnType<ReasoningApi["request"]>,
    requestStatus: (runId) =>
      post("request-status", { runId }) as ReturnType<ReasoningApi["requestStatus"]>,
    cancelRequest: (runId) =>
      post("request-cancel", { runId }) as ReturnType<ReasoningApi["cancelRequest"]>,
    reconcileRequest: (runId) =>
      post("request-reconcile", { runId, acknowledgeUncertain: true }) as ReturnType<
        ReasoningApi["reconcileRequest"]
      >,
    start: async (input) => {
      const sessionId = input.sessionId ?? crypto.randomUUID();
      return {
        sessionId,
        receipt: receipt(
          await post("start", {
            ...input,
            sessionId,
            commandId: input.commandId ?? crypto.randomUUID(),
            schemaVersion: REASONING_SCHEMA,
          }),
        ),
      };
    },
    get: async (sessionId) => {
      const response = sessionResponse.parse(await post("get", { sessionId }));
      if (!response.session) throw new Error("The reasoning session was not returned.");
      return response as ReasoningSessionResponse & {
        session: NonNullable<ReasoningSessionResponse["session"]>;
      };
    },
    byRun: async (runId) =>
      sessionResponse.parse(await post("by-run", { runId })) as ReasoningSessionResponse,
    trace: async (sessionId, afterSequence = 0, limit = 100) => {
      const response = traceResponse.parse(
        await post("trace", { sessionId, afterSequence, limit }),
      );
      return {
        ...response,
        operations: response.operations.map((operation) => ({
          ...operation,
          ...normaliseOperation(operation, sessionId),
        })),
      };
    },
    graph: async (sessionId) => graphResponse.parse(await post("graph", { sessionId })),
    execute: async (input) => {
      const { receipt: row } = (await post("execute", {
        ...input,
        commandId: input.commandId ?? crypto.randomUUID(),
      })) as { receipt: Record<string, unknown> };
      const parsed = operationRow.safeParse(row);
      return parsed.success
        ? normaliseOperation(parsed.data, input.sessionId)
        : (row as unknown as ReasoningOperation);
    },
    chain: async (sessionId) =>
      chainResponse.parse(await post("chain", { sessionId })) as ReasoningChainView,
    reviseChain: async (input) => receipt(await post("chain.revise", command(input))),
    refresh: async (input) => receipt(await post("context", command(input))),
    candidate: async (input) => receipt(await post("candidate", command(input))),
    step: async (input) =>
      receipt(
        await post("step", {
          ...command(input),
          inputRecordIds: input.inputRecordIds ?? [],
          causedByEventIds: input.causedByEventIds ?? [],
          evidence: input.evidence ?? [],
        }),
      ),
    contribute: async (input) =>
      receipt(await post("contribute", { ...command(input), evidence: input.evidence ?? [] })),
    evaluate: async (input) => receipt(await post("evaluate", command(input))),
    accept: async (input) => receipt(await post("accept", command(input))),
    assessEvidence: async (input) => receipt(await post("assess-evidence", command(input))),
    proposeReplan: async (input) => receipt(await post("replan.propose", command(input))),
    applyReplan: async (input) => receipt(await post("replan.apply", command(input))),
    correctSource: async (input) =>
      receipt(await post("correct", { ...command(input), tombstone: input.tombstone ?? false })),
    activateBranches: async (input) =>
      (
        (await post("branch.activate", command(input))) as {
          receipt: Awaited<ReturnType<ReasoningApi["activateBranches"]>>;
        }
      ).receipt,
    acknowledgeBranch: async (input) =>
      (
        (await post("branch.acknowledge", {
          ...input,
          schemaVersion: REASONING_SCHEMA,
          commandId: input.commandId ?? crypto.randomUUID(),
        })) as { receipt: Awaited<ReturnType<ReasoningApi["acknowledgeBranch"]>> }
      ).receipt,
    reportBranch: async (input) =>
      (
        (await post("branch.report", {
          ...input,
          schemaVersion: REASONING_SCHEMA,
          commandId: input.commandId ?? crypto.randomUUID(),
        })) as { receipt: Awaited<ReturnType<ReasoningApi["reportBranch"]>> }
      ).receipt,
    retryBranch: async (input) =>
      (
        (await post("branch.retry", command(input))) as {
          receipt: Awaited<ReturnType<ReasoningApi["retryBranch"]>>;
        }
      ).receipt,
    joinBranches: async (input) =>
      (
        (await post("branch.join", command(input))) as {
          receipt: Awaited<ReturnType<ReasoningApi["joinBranches"]>>;
        }
      ).receipt,
    cancelBranches: async (input) =>
      (
        (await post("branch.cancel", command(input))) as {
          receipt: Awaited<ReturnType<ReasoningApi["cancelBranches"]>>;
        }
      ).receipt,
    branchGroup: async (sessionId, groupId) =>
      (
        (await post("branch.get", { sessionId, groupId })) as {
          group: Awaited<ReturnType<ReasoningApi["branchGroup"]>>;
        }
      ).group,
    branchGroups: async (sessionId) =>
      (
        (await post("branch.list", { sessionId })) as {
          groups: Awaited<ReturnType<ReasoningApi["branchGroups"]>>;
        }
      ).groups,
    followupObjective: async (input) =>
      receipt(
        await post("objectives.followup", { ...command(input), evidence: input.evidence ?? [] }),
      ),
    submitPostmortem: (input) =>
      post("postmortem.submit", {
        ...input,
        commandId: input.commandId ?? crypto.randomUUID(),
      }) as ReturnType<ReasoningApi["submitPostmortem"]>,
    postmortem: (runId) =>
      post("postmortem.get", { runId }) as ReturnType<ReasoningApi["postmortem"]>,
    dreamerInbox: () => post("dreamer.inbox", {}) as ReturnType<ReasoningApi["dreamerInbox"]>,
    processPostmortem: (input) =>
      post("dreamer.process", input) as ReturnType<ReasoningApi["processPostmortem"]>,
    dreamerExperiments: (runId) =>
      post("dreamer.experiments", { runId }) as ReturnType<ReasoningApi["dreamerExperiments"]>,
    planDreamerExperiment: (input) =>
      post("dreamer.experiment-plan", input) as ReturnType<ReasoningApi["planDreamerExperiment"]>,
    measureDreamerExperiment: (input) =>
      post("dreamer.experiment-measure", input) as ReturnType<
        ReasoningApi["measureDreamerExperiment"]
      >,
    publishDreamerExperiment: (input) =>
      post("dreamer.experiment-publish", input) as ReturnType<
        ReasoningApi["publishDreamerExperiment"]
      >,
    rollbackDreamerExperiment: (input) =>
      post("dreamer.experiment-rollback", input) as ReturnType<
        ReasoningApi["rollbackDreamerExperiment"]
      >,
  };
}
